import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import AdaptiveListDetail from '../components/AdaptiveListDetail.jsx'
import Sheet from '../components/Sheet.jsx'
import Attachments from '../components/Attachments.jsx'
import Links from '../components/Links.jsx'
import Tags from '../components/Tags.jsx'
import {
  addBuild, addPayable, addPayout, deletePayable, deletePayout, findPayoutLocation,
  getBuilds, getMonth,
  getMonthIncome, getPayoutCategories, payableSuggestions, setPayableStatus,
  suggestionFor, updatePayable, updatePayout,
} from '../lib/db.js'
import {
  currentMonthKey, monthName, parseAmount, peso, shiftMonthKey,
} from '../lib/format.js'
import { canReceive, getAccounts } from '../lib/worth.js'

export default function Cashflow({ toast, jump = null, onJumpHandled }) {
  const [monthKey, setMonthKey] = useState(currentMonthKey())
  const [payouts, setPayouts] = useState([])
  const [payables, setPayables] = useState([])
  const [income, setIncome] = useState(0)
  const [categories, setCategories] = useState([])
  const [builds, setBuilds] = useState([])
  const [accounts, setAccounts] = useState([])
  const [suggestions, setSuggestions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [sheet, setSheet] = useState(null)
  const [selectedPayoutId, setSelectedPayoutId] = useState(null)
  const pendingJumpRef = useRef(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      /* A spinner that never resolves is the worst failure. If the database
         cannot be reached, say so instead of turning forever. */
      const [{ payouts: p, payables: i }, inc, cats] = await Promise.race([
        Promise.all([getMonth(monthKey), getMonthIncome(monthKey), getPayoutCategories()]),
        new Promise((_, reject) =>
          setTimeout(
            () => reject(new Error('No answer from the database. Check the connection and the values in .env.local.')),
            15000,
          ),
        ),
      ])
      setPayouts(p)
      setPayables(i)
      setIncome(inc)
      setCategories(cats)
      /* Builds are only needed to offer a link, so a failure here must not
         take the month down with it. */
      getBuilds().then((b) => setBuilds(b.builds.filter((x) => x.status === 'Active')))
        .catch(() => setBuilds([]))
      /* Same again for accounts: they only power the savings destination. */
      getAccounts().then(setAccounts).catch(() => setAccounts([]))
      /* And the names he has used before, which only ever suggest. */
      payableSuggestions().then(setSuggestions).catch(() => setSuggestions([]))
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }, [monthKey])

  useEffect(() => { load() }, [load])

  const catById = useMemo(
    () => Object.fromEntries(categories.map((c) => [c.id, c])),
    [categories],
  )
  const acctById = useMemo(
    () => Object.fromEntries(accounts.map((a) => [a.id, a])),
    [accounts],
  )

  /*
   * Four figures, per payout and for the month: Income, Paid, AP, Net.
   *
   * The payout IS the income. Reading income only from the Income tab was the
   * bug behind an "In" of zero and a savings rate of zero while thousands were
   * being paid — the numerator was real and the denominator was empty.
   * Anything logged in the Income tab and tied to a payout is that same money,
   * so getMonthIncome excludes it and only genuinely separate income is added
   * on top.
   *
   * Nothing here asks whether a payout has arrived. Paid and AP are facts
   * about the list, so paying a bill early out of next fortnight's payout
   * moves its Paid and leaves its Net alone, with no "received" flag to keep
   * up to date.
   */
  const stats = useMemo(() => {
    const each = payouts.map((payout, i) => {
      const mine = payables.filter((p) => p.payout_id === payout.id)
      let mobPaid = 0, swbPaid = 0, ap = 0
      for (const p of mine) {
        const amt = Number(p.amount) || 0
        if (p.status === 'Paid') {
          if (catById[p.category_id]?.kind === 'build') swbPaid += amt
          else mobPaid += amt
        } else {
          ap += amt
        }
      }
      const incomeIn = (Number(payout.salary) || 0) + (Number(payout.carried_in) || 0)
      const paid = mobPaid + swbPaid
      return {
        payout,
        /* Numbered by position, not by a stored label. A month with three
           payouts needs no migration and no special case. */
        name: 'Payout ' + (i + 1),
        rows: mine,
        income: incomeIn,
        paid, mobPaid, swbPaid, ap,
        net: incomeIn - paid - ap,
        rate: incomeIn > 0 ? Math.round((swbPaid / incomeIn) * 100) : 0,
      }
    })

    const sum = (f) => each.reduce((s, e) => s + f(e), 0)
    /* Income not attached to any payout — a gig, a sale — still belongs to
       the month even though it belongs to no payout card. */
    const incomeTotal = sum((e) => e.income) + income
    const paid = sum((e) => e.paid)
    const ap = sum((e) => e.ap)
    const swbPaid = sum((e) => e.swbPaid)
    const mobPaid = sum((e) => e.mobPaid)

    return {
      each,
      loose: income,
      month: {
        income: incomeTotal,
        paid,
        ap,
        net: incomeTotal - paid - ap,
        rate: incomeTotal > 0 ? Math.round((swbPaid / incomeTotal) * 100) : 0,
        mobPct: incomeTotal > 0 ? Math.min(100, (mobPaid / incomeTotal) * 100) : 0,
        swbPct: incomeTotal > 0 ? Math.min(100, (swbPaid / incomeTotal) * 100) : 0,
      },
    }
  }, [payables, payouts, income, catById])

  /* Optimistic tick. The build payment follows in the background. */
  async function toggle(payable, payout) {
    const paid = payable.status !== 'Paid'
    setPayables((rows) =>
      rows.map((r) => (r.id === payable.id ? { ...r, status: paid ? 'Paid' : 'Pending' } : r)),
    )
    try {
      const res = await setPayableStatus(payable, paid, payout?.label ?? null)
      if (res?.plan) {
        toast(
          res.plan.closed
            ? res.plan.name + ' is paid off'
            : res.plan.name + ' ' + res.plan.paidCount + ' of ' + res.plan.term,
        )
      } else if (res?.skippedBuild) {
        toast(res.skippedBuild + ' is finished, so nothing was logged to it.')
      } else if (res?.buildTouched) {
        toast(paid ? 'Logged to its build.' : 'Removed from its build.')
      }
      /* Say it out loud — money moving into an account should never be quiet. */
      if (payable.to_account_id) {
        const acct = acctById[payable.to_account_id]
        toast(
          paid
            ? peso(payable.amount) + ' moved into ' + (acct?.name ?? 'savings')
            : 'Taken back out of ' + (acct?.name ?? 'savings'),
        )
        getAccounts().then(setAccounts).catch(() => {})
      }
    } catch (e) {
      setPayables((rows) =>
        rows.map((r) => (r.id === payable.id ? { ...r, status: payable.status } : r)),
      )
      toast("Couldn't save that. " + (e.message || ''))
    }
  }

  /* A jump from the Log arrives with only an id. Find which month it belongs
     to first, switch to that month, and let the load run. */
  useEffect(() => {
    if (!jump || (jump.type !== 'payout' && jump.type !== 'payable')) return undefined
    let alive = true
    findPayoutLocation(jump.type, jump.id)
      .then((location) => {
        if (!alive) return
        if (!location) { onJumpHandled?.(); return }
        pendingJumpRef.current = location
        setSelectedPayoutId(null)
        setMonthKey(location.monthKey)
      })
      .catch(() => { if (alive) onJumpHandled?.() })
    return () => { alive = false }
  }, [jump, onJumpHandled])

  /* Then, once that month's rows are in, select the payout and open the
     payable if the line named one. */
  useEffect(() => {
    const pending = pendingJumpRef.current
    if (!pending || loading) return
    if (!payouts.some((payout) => payout.id === pending.payoutId)) return
    pendingJumpRef.current = null
    setSelectedPayoutId(pending.payoutId)
    if (pending.payableId) {
      const payable = payables.find((row) => row.id === pending.payableId)
      if (payable) setSheet({ kind: 'payable', payable })
    }
    onJumpHandled?.()
  }, [loading, payouts, payables, onJumpHandled])

  function shiftMonth(delta) {
    /* A payout belongs to one month. Clear it before the new month's load can
       render, so the right pane never shows stale detail under a new heading. */
    setSelectedPayoutId(null)
    setMonthKey((key) => shiftMonthKey(key, delta))
  }

  if (loading) {
    return <div className="empty"><p>Loading {monthName(monthKey)}…</p></div>
  }

  if (error) {
    return (
      <>
        <Head monthKey={monthKey} onShift={shiftMonth} />
        <div className="notice warn">
          <b>Could not read the database.</b>
          <p style={{ margin: '6px 0 0', fontSize: 14 }}>{error}</p>
          <p style={{ margin: '8px 0 0', fontSize: 13 }}>
            If the schema is not in yet, this is expected. It clears once the tables exist.
          </p>
        </div>
      </>
    )
  }

  const selectedPayout = stats.each.find((entry) => entry.payout.id === selectedPayoutId) ?? null
  const addPayoutAction = (
    <button type="button" className="btn split-new-action"
      onClick={() => setSheet({ kind: 'newPayout' })}>
      ＋ Add payout {stats.each.length + 1}
    </button>
  )

  return (
    <>
      <AdaptiveListDetail
        storageKey="mypersonalos.split.cashflowWidth"
        listLabel="Cashflow payouts"
        selectedId={selectedPayout?.payout.id ?? null}
        emptyLabel={(
          <MonthOverview
            className="cashflow-month-overview"
            monthKey={monthKey}
            stats={stats}
          />
        )}
        toolbarAction={addPayoutAction}
        list={(
          <>
            <Head monthKey={monthKey} onShift={shiftMonth} />

            {/* On iOS the month table remains above the list and scrolls
                horizontally. macOS hides this copy and gives the same table
                the full right pane whenever no payout is selected. */}
            <MonthOverview
              className="cashflow-month-ios"
              monthKey={monthKey}
              stats={stats}
            />

            {payouts.length === 0 ? (
              <div className="empty">
                <h2 className="serif">No payout yet for {monthName(monthKey)}</h2>
                <p>Add one and it starts as a copy of your last payout, plus anything due this month. Change any line, delete what does not apply.</p>
              </div>
            ) : (
              <div className="card cashflow-payout-list">
                <div className="rows">
                  {stats.each.map(({ payout, name, rows: mine }) => {
                    const selected = selectedPayout?.payout.id === payout.id
                    return (
                      <button
                        type="button"
                        className={'row payout-row' + (selected ? ' selected' : '')}
                        key={payout.id}
                        data-detail-id={payout.id}
                        aria-current={selected ? 'true' : undefined}
                        onClick={() => setSelectedPayoutId(selected ? null : payout.id)}
                      >
                        <span className="nm">
                          <b>{name}</b>
                          <span className="lbl">
                            {mine.length} {mine.length === 1 ? 'payable' : 'payables'}
                          </span>
                        </span>
                        <span className="amt num">{peso(Number(payout.salary) || 0)}</span>
                        <span className="go" aria-hidden="true">›</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </>
        )}
        detail={selectedPayout ? (
          <PayoutDetailSheet
            key={selectedPayout.payout.id}
            entry={selectedPayout}
            catById={catById}
            acctById={acctById}
            onClose={() => setSelectedPayoutId(null)}
            onEditAmount={() => setSheet({
              kind: 'salary',
              payout: selectedPayout.payout,
              name: selectedPayout.name,
              count: selectedPayout.rows.length,
            })}
            onToggle={toggle}
            onAddPayable={() => setSheet({ kind: 'newPayable', payout: selectedPayout.payout })}
            onEditPayable={(payable) => setSheet({ kind: 'payable', payable })}
          />
        ) : null}
      />

      {sheet?.kind === 'newPayout' && (
        <AmountSheet
          title={'Payout ' + (stats.each.length + 1) + ' — ' + monthName(monthKey)}
          label="Amount that came in"
          cta="Add payout"
          primary
          hint="It starts as a copy of your last payout. Edit, delete or add anything — what you leave here is what shows up next time."
          onClose={() => setSheet(null)}
          onSave={async (v) => {
            setSheet(null)
            try {
              const p = await addPayout(monthKey, v)
              await load()
              if (p?.installments) {
                toast(p.installments + (p.installments === 1
                  ? ' installment charge loaded in'
                  : ' installment charges loaded in'))
              }
            } catch (e) { toast(e.message || 'Could not add that.') }
          }}
        />
      )}

      {sheet?.kind === 'salary' && (
        <AmountSheet
          title={sheet.name}
          label="Amount"
          cta="Save"
          deleteLabel={
            sheet.count
              ? 'Delete this payout and its ' + sheet.count +
                (sheet.count === 1 ? ' payable' : ' payables')
              : 'Delete this payout'
          }
          onDelete={async () => {
            setSheet(null)
            setSelectedPayoutId(null)
            try {
              await deletePayout(sheet.payout.id)
              await load()
            } catch (e) { toast(e.message || 'Could not delete that.') }
          }}
          initial={sheet.payout.salary}
          onClose={() => setSheet(null)}
          onSave={async (v) => {
            setSheet(null)
            try {
              await updatePayout(sheet.payout.id, { salary: v })
              await load()
            } catch (e) { toast(e.message || 'Could not save that.') }
          }}
        />
      )}

      {sheet?.kind === 'newPayable' && (
        <PayableSheet
          builds={builds}
          accounts={accounts}
          suggestions={suggestions}
          toast={toast}
          title="Add payable"
          categories={categories}
          onClose={() => setSheet(null)}
          onSave={async (vals) => {
            setSheet(null)
            try {
              await addPayable({
                payoutId: sheet.payout.id,
                name: vals.name,
                amount: vals.amount,
                categoryId: vals.categoryId,
                buildId: vals.buildId ?? null,
                toAccountId: vals.toAccountId ?? null,
                sortOrder: 999,
              })
              await load()
            } catch (e) { toast(e.message || 'Could not add that.') }
          }}
        />
      )}

      {sheet?.kind === 'payable' && (
        <PayableSheet
          builds={builds}
          accounts={accounts}
          suggestions={suggestions}
          toast={toast}
          title="Edit payable"
          categories={categories}
          initial={sheet.payable}
          onClose={() => setSheet(null)}
          onDelete={async () => {
            setSheet(null)
            try {
              await deletePayable(sheet.payable.id)
              await load()
            } catch (e) { toast(e.message || 'Could not delete that.') }
          }}
          onSave={async (vals) => {
            setSheet(null)
            try {
              await updatePayable(
                sheet.payable.id,
                {
                  name: vals.name,
                  amount: vals.amount,
                  category_id: vals.categoryId,
                  build_id: vals.buildId ?? null,
                  to_account_id: vals.toAccountId ?? null,
                },
                sheet.payable,
              )
              await load()
            } catch (e) { toast(e.message || 'Could not save that.') }
          }}
        />
      )}
    </>
  )
}

/* The category heading is Money's job. This screen leads with the month,
   since that is the thing being navigated. */
function Head({ monthKey, onShift }) {
  return (
    <div className="page-head month cashflow-month-head">
      <div className="monthnav">
        <button className="navbtn" type="button" aria-label="Previous month"
          onClick={() => onShift(-1)}>‹</button>
        <b className="num">{monthName(monthKey)}</b>
        <button className="navbtn" type="button" aria-label="Next month"
          onClick={() => onShift(1)}>›</button>
      </div>
    </div>
  )
}

/* One component, two placements: the iOS list keeps its horizontal reference
   table, while the macOS nothing-selected state gives every column the full
   detail pane. */
function MonthOverview({ monthKey, stats, className = '' }) {
  return (
    <div className={'card monthbox ' + className}>
      <div className="mhead">
        <h2 className="serif">{monthName(monthKey)}</h2>
        <span className="lbl">
          {stats.each.length === 1 ? '1 payout' : stats.each.length + ' payouts'}
        </span>
      </div>

      <div className="tscroll">
        <table className="mtable">
          <thead>
            <tr>
              <th />
              {stats.each.map((entry) => <th key={entry.payout.id}>{entry.name}</th>)}
              <th className="totcol">Month</th>
            </tr>
          </thead>
          <tbody>
            <MRow label="Income" each={stats.each} pick={(entry) => entry.income}
                  total={stats.month.income} />
            <MRow label="Paid" cls="paid" each={stats.each} pick={(entry) => entry.paid}
                  total={stats.month.paid} />
            <MRow label="AP" cls="ap" each={stats.each} pick={(entry) => entry.ap}
                  total={stats.month.ap} />
            <MRow label="Net" cls="net" each={stats.each} pick={(entry) => entry.net}
                  total={stats.month.net} />
            <tr>
              <th>Wealth</th>
              {stats.each.map((entry) => (
                <td key={entry.payout.id} className={'num' + (entry.rate ? '' : ' dim')}>
                  {entry.rate}%
                </td>
              ))}
              <td className="num totcol">{stats.month.rate}%</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="rail">
        <i style={{ width: stats.month.mobPct + '%', background: 'var(--mob)' }} />
        <i style={{ width: stats.month.swbPct + '%', background: 'var(--swb)' }} />
      </div>
    </div>
  )
}

function PayoutDetailSheet({
  entry, catById, acctById, onClose, onEditAmount, onToggle, onAddPayable, onEditPayable,
}) {
  const { payout, name, rows: mine, income, paid, ap, net } = entry

  return (
    <Sheet title={name} onClose={onClose}>
      <div className="card cashflow-payout-detail">
        <div className="payout-head">
          <span className="lbl">Amount</span>
          <button type="button" className="amt num" onClick={onEditAmount}>
            {peso(Number(payout.salary) || 0)}
          </button>
        </div>

        <div className="quad">
          <div><span className="lbl">Income</span><div className="v num">{peso(income)}</div></div>
          <div><span className="lbl">Paid</span><div className={'v num' + (paid ? ' paidv' : ' dim')}>{peso(paid)}</div></div>
          <div><span className="lbl">AP</span><div className="v num apv">{peso(ap)}</div></div>
          <div><span className="lbl">Net</span><div className={'v num' + (net < 0 ? ' neg' : ' netv')}>{peso(net)}</div></div>
        </div>

        <div className="rows">
          {mine.map((payable) => {
            const category = catById[payable.category_id]
            const isPaid = payable.status === 'Paid'
            return (
              <div className={'row' + (isPaid ? ' paid' : '')} key={payable.id}
                data-context-row="">
                <button
                  type="button"
                  className="tick"
                  aria-label={isPaid ? 'Mark pending' : 'Mark paid'}
                  aria-pressed={isPaid}
                  data-context-action=""
                  onClick={() => onToggle(payable, payout)}
                >
                  ✓
                </button>
                <button type="button" className="rowopen" onClick={() => onEditPayable(payable)}>
                  <span className="nm">
                    <b>{payable.name}</b>
                    {payable.to_account_id ? (
                      <span className="lbl">
                        → {acctById[payable.to_account_id]?.name ?? 'savings'}
                      </span>
                    ) : null}
                  </span>
                  {category && <span className={'tag ' + category.kind}>{category.code}</span>}
                  <span className="amt num">{peso(payable.amount)}</span>
                  <span className="go" aria-hidden="true">›</span>
                </button>
              </div>
            )
          })}
          <button type="button" className="addline" aria-label="Add payable" onClick={onAddPayable}>
            ＋
          </button>
        </div>
      </div>
    </Sheet>
  )
}

/* One line of the month table: the same figure down every payout, then the
   month's own. Zero is dimmed rather than hidden, because a payout with
   nothing paid yet is information, not an empty cell. */
function MRow({ label, cls = '', each, pick, total }) {
  return (
    <tr>
      <th>{label}</th>
      {each.map((e) => {
        const v = pick(e)
        return (
          <td key={e.payout.id} className={'num ' + (v ? cls : 'dim')}>
            {peso(v)}
          </td>
        )
      })}
      <td className={'num totcol ' + (total < 0 ? 'neg' : cls)}>{peso(total)}</td>
    </tr>
  )
}

function AmountSheet({ title, label, cta, hint, initial, onClose, onSave, onDelete, deleteLabel, primary = false }) {
  const [v, setV] = useState(initial ? String(Math.round(Number(initial))) : '')
  return (
    <Sheet title={title} onClose={onClose}>
      <div className="field">
        <label className="lbl" htmlFor="amt">{label}</label>
        <input id="amt" inputMode="numeric" autoComplete="off" placeholder="₱0"
          value={v} onChange={(e) => setV(e.target.value)} />
      </div>
      {hint && <p className="hint" style={{ margin: '0 0 4px' }}>{hint}</p>}
      <div className="sheet-actions">
        <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
        <button type="button" className="btn" data-sheet-primary={primary || undefined}
          onClick={() => onSave(parseAmount(v))}>{cta}</button>
      </div>
      {/* Deleting a payout takes its payables with it. The button counts them
          rather than letting him find out afterwards — the rare destructive
          thing carries the friction, deleting one line does not. */}
      {onDelete && (
        <button type="button" className="btn danger wide" onClick={onDelete}>
          {deleteLabel}
        </button>
      )}
    </Sheet>
  )
}

/*
 * Detection offers, never acts. A payable whose name matches an active build
 * gets a quiet line suggesting the link; one that matches nothing offers to
 * start a build. Both need his tap, and creating one still needs a target he
 * types himself.
 */
function PayableSheet({
  title, categories, builds = [], accounts = [], suggestions = [],
  initial, onClose, onSave, onDelete, toast,
}) {
  const [name, setName] = useState(initial?.name ?? '')
  const [amount, setAmount] = useState(
    initial?.amount ? String(Math.round(Number(initial.amount))) : '',
  )
  const [categoryId, setCategoryId] = useState(
    initial?.category_id ?? categories[0]?.id ?? '',
  )
  const [buildId, setBuildId] = useState(initial?.build_id ?? null)
  const [toAccountId, setToAccountId] = useState(initial?.to_account_id ?? '')
  const [filled, setFilled] = useState(null)
  const [offerTarget, setOfferTarget] = useState('')
  const [making, setMaking] = useState(false)

  const typed = name.trim().toLowerCase()
  const match = typed.length > 2
    ? builds.find((b) => b.name.toLowerCase() === typed) ||
      builds.find((b) => b.name.toLowerCase().includes(typed) || typed.includes(b.name.toLowerCase()))
    : null
  const linked = buildId ? builds.find((b) => b.id === buildId) : null

  const destinations = accounts.filter(canReceive)
  const dest = toAccountId ? destinations.find((a) => a.id === toAccountId) : null
  const swb = categories.find((c) => c.kind === 'build')
  /* Changing where a paid push went would strand the deposit it already made. */
  const lockedPaid = initial?.status === 'Paid' && Boolean(initial?.to_account_id)

  return (
    <Sheet title={title} onClose={onClose}>
      <div className="field">
        <label className="lbl" htmlFor="pname">Name</label>
        {/* Completes from every line he has ever written. Choosing one brings
            back what it was worth last time — into an empty amount only, so it
            can never quietly overwrite a figure he has already typed. */}
        <input id="pname" autoComplete="off" list="payable-names" value={name}
          onChange={(e) => {
            const v = e.target.value
            setName(v)
            const s = suggestionFor(suggestions, v)
            if (!s || amount.trim()) return
            setAmount(String(Math.round(s.amount)))
            if (s.categoryId) setCategoryId(s.categoryId)
            if (s.buildId) setBuildId(s.buildId)
            if (s.toAccountId) setToAccountId(s.toAccountId)
            setFilled(s)
          }} />
        <datalist id="payable-names">
          {suggestions.map((s) => <option key={s.key} value={s.name} />)}
        </datalist>
        {filled && filled.key === name.trim().toLowerCase() ? (
          <p className="hint">
            Filled in from the last time you used this
            {filled.uses > 1 ? ' — ' + filled.uses + ' times so far' : ''}. Change anything.
          </p>
        ) : null}
      </div>
      <div className="field">
        <label className="lbl" htmlFor="pamt">Amount</label>
        <input id="pamt" inputMode="numeric" autoComplete="off" placeholder="₱0"
          value={amount} onChange={(e) => setAmount(e.target.value)} />
      </div>
      <div className="field">
        <label className="lbl" htmlFor="pcat">Category</label>
        <select id="pcat" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
          ))}
        </select>
      </div>
      {/* Savings, wallet and investment only. Paying down a card or a loan is
          an expense he already tracks as an MOB line, so those stay out. */}
      {destinations.length ? (
        <div className="field">
          <label className="lbl" htmlFor="pdest">Push to savings</label>
          <select id="pdest" value={toAccountId} disabled={lockedPaid}
            onChange={(e) => setToAccountId(e.target.value)}>
            <option value="">No — this is an expense</option>
            {destinations.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}{a.institution ? ' · ' + a.institution : ''}
              </option>
            ))}
          </select>
          {lockedPaid ? (
            <p className="hint">
              Already paid, so the money is in {dest?.name || 'that account'} already.
              Untick it first to change where it goes.
            </p>
          ) : dest ? (
            <p className="hint">
              Ticking this paid moves {peso(parseAmount(amount))} into {dest.name} and takes
              it out of what is left. Unticking takes it back.
            </p>
          ) : null}
          {dest && swb && categoryId !== swb.id ? (
            <div className="notice" style={{ margin: '10px 0 0' }}>
              <p style={{ margin: 0, fontSize: 13 }}>
                Savings usually sits in {swb.code}, which is what your savings rate counts.{' '}
                <button type="button" className="linkish" style={{ display: 'inline' }}
                  onClick={() => setCategoryId(swb.id)}>Move it</button>
              </p>
            </div>
          ) : null}
        </div>
      ) : null}

      {toAccountId ? null : linked ? (
        <p className="lbl" style={{ margin: '-4px 0 14px' }}>
          Paying this logs to {linked.name}.{' '}
          <button type="button" className="linkish" style={{ display: 'inline' }}
            onClick={() => setBuildId(null)}>Unlink</button>
        </p>
      ) : match ? (
        <div className="notice" style={{ margin: '-4px 0 14px' }}>
          <p style={{ margin: 0, fontSize: 13 }}>
            Is this for <b>{match.name}</b>?{' '}
            <button type="button" className="linkish" style={{ display: 'inline' }}
              onClick={() => setBuildId(match.id)}>Link it</button>
          </p>
        </div>
      ) : typed.length > 2 && !making ? (
        <p className="lbl" style={{ margin: '-4px 0 14px' }}>
          Not a build yet.{' '}
          <button type="button" className="linkish" style={{ display: 'inline' }}
            onClick={() => setMaking(true)}>Make one?</button>
        </p>
      ) : null}

      {making ? (
        <div className="notice" style={{ margin: '-4px 0 14px' }}>
          <p className="lbl" style={{ margin: '0 0 8px' }}>New build — what are you raising?</p>
          <input inputMode="numeric" placeholder="Target" value={offerTarget}
            onChange={(e) => setOfferTarget(e.target.value)}
            style={{ width: '100%', border: '1px solid var(--rule)', borderRadius: 9, padding: '10px 12px', fontSize: 16, background: 'var(--screen)' }} />
          <div className="sheet-actions" style={{ marginTop: 10 }}>
            <button type="button" className="btn ghost" onClick={() => setMaking(false)}>
              Not now
            </button>
            <button type="button" className="btn" disabled={!parseAmount(offerTarget)}
              data-sheet-primary={!initial?.id || undefined}
              onClick={async () => {
                const b = await addBuild({ name: name.trim(), target: parseAmount(offerTarget) })
                setBuildId(b.id)
                setMaking(false)
              }}>
              Start it
            </button>
          </div>
        </div>
      ) : null}

      {initial?.id ? (
        <>
          <Tags target={{ type: 'payable', id: initial.id }} toast={toast} />
          <Links target={{ type: 'payable', id: initial.id }} toast={toast} compact />
          <Attachments target={{ type: 'payable', id: initial.id }} toast={toast} compact />
        </>
      ) : null}

      <div className="sheet-actions">
        {onDelete
          ? <button type="button" className="btn ghost" onClick={onDelete}>Delete</button>
          : <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>}
        <button
          type="button"
          className="btn"
          data-sheet-primary={!initial?.id && !making || undefined}
          onClick={() => {
            if (!name.trim()) return
            onSave({
              name: name.trim(),
              amount: parseAmount(amount),
              categoryId: categoryId || null,
              buildId: toAccountId ? null : buildId,
              toAccountId: toAccountId || null,
            })
          }}
        >
          Save
        </button>
      </div>
    </Sheet>
  )
}
